import {useEffect, useRef} from 'react';

import {DataFrame} from 'data-frames/DataFrameTypes';
import {DATA_FRAME_TABLE_CONSTANTS} from 'data-frames/table/DataFrameTable';
import {DataFrameSortInputVariable} from 'data-frames/util/toDataFrameSortInput';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {readQuerySafe, writeQuery} from 'graphql-client/ApolloWrappers';
import QueryWrapper from 'graphql-client/QueryWrapper';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import withPromisePool from 'util/withPromisePool';

import {GetPersistedDataFrame, GetPersistedDataFrameVariables} from './__generated/GetPersistedDataFrame';
import {GET_PERSISTED_DATA_FRAME_QUERY} from './GetPersistedDataFrameQuery';

const {MAX_ROWS_PER_PAGE} = DATA_FRAME_TABLE_CONSTANTS;
// How long to let `rowsOffset` settle before prefetching, so a burst of rapid paging clicks collapses into
// a single classification (single-page step vs. jump) instead of being decided by whichever renders
// happened to commit during the burst.
const ROWS_OFFSET_DEBOUNCE_MS = 300;
// How many pages ahead/behind the current page to keep warm in the cache.
const PREFETCH_PAGE_RADIUS = 5;
// On first mount (or after a sort/data-frame change resets to page 1), there's no "behind" to prefetch, so
// the window only extends forward: the current page plus the next `PREFETCH_PAGE_RADIUS` pages.
const INITIAL_WINDOW_PAGE_COUNT = PREFETCH_PAGE_RADIUS + 1;
// On a multi-page jump, prefetch a window centered on the destination page.
const JUMP_WINDOW_PAGE_COUNT = PREFETCH_PAGE_RADIUS * 2 + 1;

const wrappedQuery: QueryWrapper<GetPersistedDataFrame, GetPersistedDataFrameVariables> = {
  query: GET_PERSISTED_DATA_FRAME_QUERY,
};

// Cap concurrent prefetch requests so a page/sort change doesn't burst several requests at once.
const queryForPrefetch = withPromisePool(
  (variables: GetPersistedDataFrameVariables) =>
    getApolloClient().query<GetPersistedDataFrame, GetPersistedDataFrameVariables>({
      query: GET_PERSISTED_DATA_FRAME_QUERY,
      variables,
      fetchPolicy: 'cache-first',
    }),
  2
);

function isPageCached(variables: GetPersistedDataFrameVariables): boolean {
  return readQuerySafe(wrappedQuery, {variables}) != null;
}

// Prefetching is a pure optimization; a failure here just means the real page-view query (already handled
// by the caller's own error UI) will pay the network cost when the user actually gets there.
function ignorePrefetchFailure(): void {}

/** Slice a full `DataFrame`'s rows into a single 100-row page, keeping all other fields intact. */
function sliceDataFramePage(dataFrame: DataFrame, pageOffsetWithinSlice: number): DataFrame {
  return {
    ...dataFrame,
    rows: dataFrame.rows.slice(pageOffsetWithinSlice, pageOffsetWithinSlice + MAX_ROWS_PER_PAGE),
  };
}

/**
 * Fetch a `pageCount * MAX_ROWS_PER_PAGE`-row window starting at `windowOffset`, then seed the cache with
 * one entry per `MAX_ROWS_PER_PAGE`-row page within it, so that paging through those pages later resolves
 * from cache with no additional network request.
 */
async function prefetchWindow(
  dataFrameId: string,
  windowOffset: number,
  pageCount: number,
  sortInput: DataFrameSortInputVariable | null
): Promise<void> {
  const windowRows = pageCount * MAX_ROWS_PER_PAGE;
  const result = await getApolloClient().query<GetPersistedDataFrame, GetPersistedDataFrameVariables>({
    query: GET_PERSISTED_DATA_FRAME_QUERY,
    variables: {dataFrameId, limit: windowRows, offset: windowOffset, sortInput},
    fetchPolicy: 'network-only',
  });
  const {dataFrame} = result.data;
  if (dataFrame.__typename !== 'DataFrameNode') {
    return;
  }

  for (
    let pageOffset = windowOffset;
    pageOffset < windowOffset + windowRows;
    pageOffset += MAX_ROWS_PER_PAGE
  ) {
    const pageOffsetWithinSlice = pageOffset - windowOffset;
    if (pageOffsetWithinSlice >= dataFrame.dataFrameJson.rows.length) {
      break;
    }
    writeQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>(
      wrappedQuery,
      {
        dataFrame: {
          ...dataFrame,
          dataFrameJson: sliceDataFramePage(dataFrame.dataFrameJson, pageOffsetWithinSlice),
        },
      },
      {dataFrameId, limit: MAX_ROWS_PER_PAGE, offset: pageOffset, sortInput}
    );
  }
}

/**
 * Keep pages near the current page warm in Apollo's cache, so paging through them resolves instantly with
 * no network round-trip.
 *
 * `rowsOffset` is debounced before use, so a burst of rapid paging clicks settles into one classification
 * (rather than each intermediate offset racing to be classified by whichever renders happen to commit
 * during the burst) and doesn't launch an uncapped `prefetchWindow` request per click.
 *
 * - On mount, or whenever the data frame or sort changes (which resets to page 1 under a new row order),
 *   there's no "behind" to prefetch yet, so this fetches a forward-only window: the current page plus the
 *   next `PREFETCH_PAGE_RADIUS` pages, in one request.
 * - On a single-page step (Prev/Next), fetches just the one page that newly entered the window, if it isn't
 *   already cached.
 * - On a multi-page jump (e.g. typing a page number) within the same data frame/sort, fetches a full window
 *   centered on the new page.
 */
export default function usePrefetchPersistedDataFramePages(
  dataFrameId: string,
  rowsOffset: number,
  totalRows: number,
  sortInput: DataFrameSortInputVariable | null,
  enabled: boolean
): void {
  const previousPrefetchedRef = useRef<{
    dataFrameId: string;
    sortInput: DataFrameSortInputVariable | null;
    rowsOffset: number;
  } | null>(null);

  const debouncedRowsOffset = useDebounce(rowsOffset, ROWS_OFFSET_DEBOUNCE_MS);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const currentPage = Math.floor(debouncedRowsOffset / MAX_ROWS_PER_PAGE);
    const previousPrefetched = previousPrefetchedRef.current;
    const isSameDataFrameAndSort =
      previousPrefetched != null &&
      previousPrefetched.dataFrameId === dataFrameId &&
      sortInputsAreEqual(previousPrefetched.sortInput, sortInput);
    const previousPage = isSameDataFrameAndSort
      ? Math.floor(previousPrefetched.rowsOffset / MAX_ROWS_PER_PAGE)
      : null;
    const isSinglePageStep = previousPage != null && Math.abs(currentPage - previousPage) === 1;

    previousPrefetchedRef.current = {dataFrameId, sortInput, rowsOffset: debouncedRowsOffset};

    const maxOffset = Math.max(0, totalRows - MAX_ROWS_PER_PAGE);

    if (isSinglePageStep) {
      const edgePageOffset =
        (currentPage + (currentPage > previousPage ? PREFETCH_PAGE_RADIUS : -PREFETCH_PAGE_RADIUS)) *
        MAX_ROWS_PER_PAGE;
      const clampedOffset = Math.min(Math.max(edgePageOffset, 0), maxOffset);
      const variables: GetPersistedDataFrameVariables = {
        dataFrameId,
        limit: MAX_ROWS_PER_PAGE,
        offset: clampedOffset,
        sortInput,
      };
      if (!isPageCached(variables)) {
        queryForPrefetch(variables).catch(ignorePrefetchFailure);
      }
    } else if (!isSameDataFrameAndSort) {
      // Initial load (or a new data frame/sort): no pages behind the current one have ever been fetched, so
      // only prefetch forward.
      const windowOffset = Math.min(currentPage * MAX_ROWS_PER_PAGE, maxOffset);
      prefetchWindow(dataFrameId, windowOffset, INITIAL_WINDOW_PAGE_COUNT, sortInput).catch(
        ignorePrefetchFailure
      );
    } else {
      // Multi-page jump (e.g. typing a page number): fetch a window centered on the destination page.
      const windowOffset = Math.min(
        Math.max(currentPage - PREFETCH_PAGE_RADIUS, 0) * MAX_ROWS_PER_PAGE,
        maxOffset
      );
      prefetchWindow(dataFrameId, windowOffset, JUMP_WINDOW_PAGE_COUNT, sortInput).catch(
        ignorePrefetchFailure
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally comparing against the ref, not re-running on every render
  }, [dataFrameId, debouncedRowsOffset, sortInput, totalRows, enabled]);
}

function sortInputsAreEqual(
  a: DataFrameSortInputVariable | null,
  b: DataFrameSortInputVariable | null
): boolean {
  if (a == null || b == null) {
    return a === b;
  }
  return a.columnSystemName === b.columnSystemName && a.descending === b.descending;
}
